if (!customElements.get("m-slideshow")) {
  class MSlideshow extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.autoplay = this.dataset.autoplay === "true";
      this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed);
      this.showArrows = this.dataset.enableArrows === "true";
      this.showDots = this.dataset.enableDots === "true";
      this.adaptHeight = this.dataset.slideHeight === "adapt";
      /* koskii-style options, read from the section. Defaults are exactly what this
         file did before, so every other slider in the theme is unchanged. */
      this.fx = this.dataset.effect || "fade";                       // "fade" | "slide"
      this.fxSpeed = parseInt(this.dataset.transitionSpeed || "1000", 10);
      this.loopMode = this.dataset.loopMode || "loop";               // "loop" | "rewind"
      this.rewindSpeed = parseInt(this.dataset.rewindSpeed || "400", 10);
      /* Where the autoplay clock starts. Swiper counts its delay from the moment a
         transition ENDS, so a slide reached by Swiper is on screen for
         transition + delay, while a slide reached by our own rewind timer below
         (which starts counting at the move) gets delay alone. On a three-slide
         hero that made the middle slide sit a full second longer than the other
         two — measured. "start" evens them out by taking the transition back off
         Swiper's delay. Default is "end", i.e. exactly what this file did before,
         so the header's slider is untouched. */
      this.autoplayBasis = this.dataset.autoplayBasis || "end";
      this.id = this.dataset.id;
      this.sliderContainer = this.querySelector(".swiper-container");

      this.lastVideo = null;
      this.lastActive = null;
      this.timeout = null;
      this.initSlider();
    }

    initSlider() {
      this.slider = new MinimogLibs.Swiper(this.sliderContainer, {
        init: false,
        autoplay: this.autoplay
          ? {
              delay:
                this.autoplayBasis === "start"
                  ? Math.max(100, this.autoplaySpeed * 1000 - this.fxSpeed)
                  : this.autoplaySpeed * 1000,
              disableOnInteraction: true,
            }
          : false,
        slidesPerView: 1,
        slidesPerGroup: 1,
        loop: this.loopMode !== "rewind",
        effect: this.fx,
        fadeEffect: {
          crossFade: true,
        },
        speed: this.fxSpeed,
        navigation: this.showArrows
          ? {
              nextEl: this.querySelector(".m-slider-controls__button-next"),
              prevEl: this.querySelector(".m-slider-controls__button-prev"),
            }
          : false,
        pagination: this.showDots
          ? {
              el: this.querySelector(".swiper-pagination"),
              clickable: true,
              bulletClass: "m-dot",
              bulletActiveClass: "m-dot--active",
            }
          : false,
        breakpoints: {
          992: {
            threshold: 2,
          },
        },
        on: {
          /* "rewind": stop at the last slide and wind the whole rail back to the
             first, which is what koskii does — a visible ~400ms run backwards
             rather than Swiper's invisible jump. */
          reachEnd: () => {
            if (this.loopMode !== "rewind") return;
            clearTimeout(this._rewind);
            this._rewind = setTimeout(() => {
              if (this.slider && this.slider.activeIndex > 0) {
                this.slider.slideTo(0, this.rewindSpeed);
                /* The rewind animation still has rewindSpeed to run. On the "end"
                 basis Swiper's own delay covers that, but on "start" the delay has
                 already had the transition subtracted — starting the clock here
                 would put the first slide on screen for that much less. Wait out
                 the wind-back, then start. */
              if (this.slider.autoplay && this.autoplay) {
                if (this.autoplayBasis === "start") {
                  clearTimeout(this._resume);
                  /* Wait fxSpeed, NOT rewindSpeed. The first slide is on screen for
                     (this wait + the shortened delay), and the delay had exactly
                     fxSpeed taken off it — so fxSpeed is what puts it back. Using
                     rewindSpeed only looks right while the two happen to be equal;
                     the moment the owner sets a different wind-back speed, the
                     first slide would drift by the difference. */
                  this._resume = setTimeout(() => {
                    if (this.slider && this.slider.autoplay) this.slider.autoplay.start();
                  }, this.fxSpeed);
                } else {
                  this.slider.autoplay.start();
                }
              }
              }
            }, this.autoplaySpeed * 1000);
          },
          init() {
            setTimeout(() => {
              window.dispatchEvent(new Event("resize"));
            }, 100);
          },
        },
      });

      this.lastSlide = null;
      this.currentSlide = null;
      this.slider && this.slider.on("init", this.handleChange.bind(this));
      this.slider && this.slider.on("slideChange", this.handleChange.bind(this));
      this.slider.init();

      if (Shopify.designMode) {
        document.addEventListener("shopify:block:select", (e) => this.onBlockSelect(e));
      }
    }

    handleChange(swiper) {
      const { activeIndex, slides } = swiper;
      const slideType = slides[activeIndex] && slides[activeIndex].dataset.slideType;
      this.lastActive && this.slider.slideToLoop(this.lastActive);
      if (slideType === "video_slide") {
        const video = slides[activeIndex] && slides[activeIndex].querySelector("video");

        if (video) {
          let playPromise = video.play();
          this.lastVideo && this.lastVideo.pause();
          if (playPromise !== undefined) {
            playPromise.then(() => {
              this.lastVideo = video;
            });
          }
        }
      } else {
        this.lastVideo && this.lastVideo.pause();
        this.lastVideo = null;
      }
    }

    onBlockSelect(ev) {
      const block = ev.target;
      const index = Number(block.dataset.slide);
      this.lastActive = index;
      if (this.slider) {
        this.slider.slideToLoop(index);
      }
    }
  }

  customElements.define("m-slideshow", MSlideshow);
}
